import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { useCallback, useState } from 'react';
import { api } from '../../../shared/services/api';
import type { IResponseAjud } from '../../../shared/services/types/response';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface PendingAction {
  type: 'manual_acceptance' | 'generate_response';
  recordId: string;
  retainedValue?: number;
  courtOrder?: string;
}

interface UseAjudActionsReturn {
  handleManualAcceptance: (recordId: string, retainedValue: number, courtOrder: string) => void;
  handleGenerateResponse: (recordId: string, courtOrder: string) => void;
  isProcessing: boolean;
  ConfirmationDialog: React.FC;
  FeedbackSnackbar: React.FC;
}

export const useAjudActions = (onSuccess: () => void): UseAjudActionsReturn => {
  const token = Cookies.get('@token');
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showFeedback = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- Aceite Manual ---
  const handleManualAcceptance = (recordId: string, retainedValue: number, courtOrder: string) => {
    setPendingAction({
      type: 'manual_acceptance',
      recordId,
      retainedValue,
      courtOrder,
    });
  };

  // --- Gerar Resposta ---
  const handleGenerateResponse = (recordId: string, courtOrder: string) => {
    setPendingAction({
      type: 'generate_response',
      recordId,
      courtOrder,
    });
  };

  const executeAction = useCallback(async () => {
    if (!pendingAction) return;

    const headers = { Authorization: 'Bearer ' + token };

    try {
      setIsProcessing(true);

      if (pendingAction.type === 'manual_acceptance') {
        await api.put(
          `/ajudArchive/manualAcceptance/${pendingAction.recordId}`,
          { retained_value: pendingAction.retainedValue },
          { headers },
        );
        showFeedback(
          `Aceite manual realizado com sucesso. Protocolo ${pendingAction.courtOrder} agora está PRONTO PARA PROCESSAR.`,
          'success',
        );
      } else if (pendingAction.type === 'generate_response') {
        await api.put(
          `/ajudArchive/generateResponse/${pendingAction.recordId}`,
          {},
          { headers },
        );
        showFeedback(
          `Resposta gerada com sucesso para o protocolo ${pendingAction.courtOrder}.`,
          'success',
        );
      }

      onSuccess();
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        showFeedback(
          err.response?.data?.message || 'Erro ao processar a operação.',
          'error',
        );
      } else {
        showFeedback('Erro inesperado ao processar a operação.', 'error');
      }
    } finally {
      setIsProcessing(false);
      setPendingAction(null);
    }
  }, [pendingAction, token, onSuccess]);

  const cancelAction = () => {
    setPendingAction(null);
  };

  const confirmationMessage =
    pendingAction?.type === 'manual_acceptance'
      ? `Tem certeza que deseja confirmar o aceite manual do protocolo ${pendingAction.courtOrder}? O status será alterado para PRONTO PARA PROCESSAR.`
      : pendingAction?.type === 'generate_response'
        ? `Tem certeza que deseja processar esta transferência para o protocolo ${pendingAction?.courtOrder}?`
        : '';

  const ConfirmationDialog: React.FC = () => (
    <Dialog
      open={pendingAction !== null}
      onClose={cancelAction}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Confirmação</DialogTitle>
      <DialogContent>
        <DialogContentText>{confirmationMessage}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelAction} color="inherit" disabled={isProcessing}>
          Cancelar
        </Button>
        <Button
          onClick={executeAction}
          variant="contained"
          color="primary"
          disabled={isProcessing}
        >
          {isProcessing ? 'Processando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const FeedbackSnackbar: React.FC = () => (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={closeSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        onClose={closeSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  return {
    handleManualAcceptance,
    handleGenerateResponse,
    isProcessing,
    ConfirmationDialog,
    FeedbackSnackbar,
  };
};
